import {useMemo} from 'react';
import {Button} from '@mui/material';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  BasketballLeagueAdminDTO,
  useGetBasketballLeagues,
  useUpdateBasketballLeagueQuarterMinutes,
} from '@entities/basketball';
import {getErrorMessageFromApiError} from '@shared/api';
import {centerAlignmentColumnProps, MrTable} from '@shared/components';
import {entityTableCommonProps} from '@shared/constants';
import {Header} from '@widgets/common';
import {openEditQuarterMinutesDialog} from './EditQuarterMinutesDialog';

const localStorageKey = 'basketballLeaguesTableV1';

export const BasketballLeaguesPage = () => {
  const {enqueueSnackbar} = useSnackbar();
  const {data, isPending: isLoading, error} = useGetBasketballLeagues();
  const {mutateAsync: updateQuarterMinutes} =
    useUpdateBasketballLeagueQuarterMinutes();

  const columns = useMemo<MRT_ColumnDef<BasketballLeagueAdminDTO>[]>(() => {
    const handleEdit = async (league: BasketballLeagueAdminDTO) => {
      let quarterMinutes;

      try {
        quarterMinutes = await openEditQuarterMinutesDialog({
          leagueName: league.name,
          quarterMinutes: league.quarter_minutes,
        });
      } catch {
        // Dialog cancelled
        return;
      }

      try {
        await updateQuarterMinutes({leagueId: league.id, quarterMinutes});
        enqueueSnackbar('Quarter minutes updated', {variant: 'success'});
      } catch (err) {
        const errorMessage = isAxiosError(err)
          ? getErrorMessageFromApiError(err)
          : '';

        enqueueSnackbar(`Could not update quarter minutes. ${errorMessage}`, {
          variant: 'error',
        });
      }
    };

    return [
      {accessorKey: 'name', header: 'Name', size: 260},
      {
        accessorKey: 'api_sports_id',
        header: 'API-Sports ID',
        Cell: ({row}) => row.original.api_sports_id ?? '—',
        size: 140,
      },
      {
        accessorKey: 'quarter_minutes',
        header: 'Quarter minutes',
        size: 140,
        ...centerAlignmentColumnProps,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        Cell: ({row}) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              void handleEdit(row.original);
            }}
          >
            Edit
          </Button>
        ),
        size: 100,
      },
    ];
  }, [enqueueSnackbar, updateQuarterMinutes]);

  return (
    <MrTable
      columns={columns}
      data={data ?? []}
      error={error}
      renderTopToolbarCustomActions={() => (
        <Header title="Basketball leagues" isBackButtonShown={false} />
      )}
      localStorageKeyForSettings={localStorageKey}
      state={{isLoading}}
      {...entityTableCommonProps}
      enableRowActions={false}
    />
  );
};
